import type { GuidelineToSave } from "../../components/chat/SaveAllGuidelinesPanel";
import { getActionIdentifier, getMessageType } from "../../types/chat";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrawerExpandedInConversation } from "./use-drawer-expanded-in-conversation";
import { saveActionOutcomeOrToast } from "../../utils/action-outcome-save";
import type { ChatMessage } from "../../types/chat";
import type { ToolStatusSnapshot } from "@common/components/chat/groupAssistantSteps";
import type { useChat } from "../../context/ChatContext";

export interface UseGuidelineSuggestionsInputs extends Pick<
  ReturnType<typeof useChat>,
  "isActionApplied" | "isActionDismissed" | "markActionAsApplied" | "markActionAsDismissed"
> {
  messages: ChatMessage[];
  toolStatusByCallId: Map<string, ToolStatusSnapshot>;
  conversationId: string;
}

export function useGuidelineSuggestions({ messages, toolStatusByCallId, isActionApplied, isActionDismissed, markActionAsApplied, markActionAsDismissed, conversationId }: UseGuidelineSuggestionsInputs) {
  const [guidelinesToSave, setGuidelinesToSave] = useState<GuidelineToSave[]>([]);
  const [isGuidelinesPanelExpanded, setIsGuidelinesPanelExpanded] = useDrawerExpandedInConversation(conversationId, guidelinesToSave.length > 0);
  const derivedGuidelines = useMemo<GuidelineToSave[]>(() => {
    const guidelines: GuidelineToSave[] = [];

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      if (message.turnType === "assistant") {
        const messageType = getMessageType(message);
        const toolName = message.toolName;
        const messageId = getActionIdentifier(message, `msg-${i}`);

        if (messageType === "tool_result" && toolName === "send_guideline_suggestion") {
          const tcid = (message.turnArtifacts as any)?.tool_call_id;
          if (tcid && toolStatusByCallId.get(tcid)?.status === "error") continue;

          const isApplied = isActionApplied(conversationId, messageId);
          const isDismissed = isActionDismissed(conversationId, messageId);

          try {
            const data = JSON.parse(message.content);
            guidelines.push({
              id: messageId,
              name: data.name,
              description: data.description,
              category: data.category,
              rule: data.rule,
              comment: data.comment || "",
              importance: data.importance,
              playbookId: data.playbook_id,
              isSaved: isApplied,
              isDismissed: isDismissed,
              isSelected: !isApplied && !isDismissed
            });
          } catch {
            continue;
          }
        }
      }
    }

    return guidelines;
  }, [messages, toolStatusByCallId, conversationId, isActionApplied, isActionDismissed]);
  const guidelinesSignature = JSON.stringify([
    conversationId,
    derivedGuidelines.map(g => [
      g.id,
      g.name,
      g.description,
      g.category,
      g.rule,
      g.comment,
      g.importance,
      g.playbookId,
      g.isSaved,
      g.isDismissed
    ])
  ]);
  const derivedGuidelinesRef = useRef(derivedGuidelines);
  derivedGuidelinesRef.current = derivedGuidelines;
  useEffect(() => {
    setGuidelinesToSave(derivedGuidelinesRef.current);
  }, [guidelinesSignature]);
  const handleGuidelineSaved = useCallback((guidelineId: string) => {
    markActionAsApplied(conversationId, guidelineId);

    setGuidelinesToSave(prev => prev.map(g =>
      g.id === guidelineId ? { ...g, isSaved: true } : g
    ));
  }, [conversationId, markActionAsApplied]);
  const handleGuidelineDismissed = useCallback((guidelineId: string) =>
    saveActionOutcomeOrToast(() => markActionAsDismissed(conversationId, guidelineId)), [conversationId, markActionAsDismissed]);
  const handleToggleGuidelineSelection = useCallback((guidelineId: string) => {
    setGuidelinesToSave(prev => prev.map(g =>
      g.id === guidelineId ? { ...g, isSelected: !g.isSelected } : g
    ));
  }, []);
  const handleToggleSelectAllGuidelines = useCallback(() => {
    setGuidelinesToSave(prev => {
      const unsavedUndismissed = prev.filter(g => !g.isSaved && !g.isDismissed);
      const allSelected = unsavedUndismissed.every(g => g.isSelected);
      return prev.map(g =>
        (g.isSaved || g.isDismissed) ? g : { ...g, isSelected: !allSelected }
      );
    });
  }, []);
  return { isGuidelinesPanelExpanded, setIsGuidelinesPanelExpanded, guidelinesToSave, handleGuidelineSaved, handleGuidelineDismissed, handleToggleGuidelineSelection, handleToggleSelectAllGuidelines };
}
